// Dependency-free glob matching for the local topic cache. The CLI ships with
// zero runtime deps, so this replicates the minimatch subset that anchor
// patterns actually use — *, ?, **, literals — with minimatch's semantics:
// wildcards (including **) never match a dot-segment, a trailing /** requires
// at least one extra segment, and a mid-path /**/ matches zero or more.
// A parity spec (glob-match.spec.ts) gates every rule against real minimatch.
//
// Patterns using constructs we don't implement ([abc], {a,b}, extglobs) fall
// back to a permissive per-segment wildcard. That direction is deliberate:
// over-matching adds a topic to a budget-capped brief (harmless); an
// under-match would silently drop team context (harmful).

const compiled = new Map<string, RegExp>()

// Constructs we deliberately do NOT implement: char classes, braces, extglobs.
// Found at PATTERN level (not per segment) — fuzzing proved brace expansion can
// collapse/escape segment structure ({a,..} sequence-expands into nearly
// match-anything), so a per-segment fallback can under-match.
const UNSUPPORTED = /[[\]{}()!@+|]/

function segToRegexSrc(seg: string): string {
  const esc = seg.replace(/[.+^${}()|[\]\\]/g, '\\$&').replace(/\*+/g, '[^/]*').replace(/\?/g, '[^/]')
  // A wildcard-leading segment never matches a dot-segment (minimatch default).
  return /^[*?]/.test(seg) ? `(?!\\.)${esc}` : esc
}

export function globToRegExp(pattern: string): RegExp {
  let re = compiled.get(pattern)
  if (re) return re
  const segs = pattern.split('/').filter((s) => s !== '')
  let src = '^'
  let needSlash = false
  for (let i = 0; i < segs.length; i++) {
    const seg = segs[i]
    const isLast = i === segs.length - 1
    if (seg === '**') {
      if (isLast) {
        // Trailing ** — at least one further segment, none dot-leading. After a
        // real segment that means a leading slash ('a/**' ≠ 'a'); at the start
        // or after a mid-globstar there is no slash to require ('**/**'
        // matches 'a' — fuzz-found case).
        src += needSlash ? '(?:/(?!\\.)[^/]+)+' : '(?!\\.)[^/]+(?:/(?!\\.)[^/]+)*'
        needSlash = true
      } else {
        // Mid-path ** — zero or more segments, each non-dot, each slash-closed:
        // 'a/**/b' must match both a/b and a/x/y/b.
        src += (needSlash ? '/' : '') + '(?:(?!\\.)[^/]+/)*'
        needSlash = false
      }
    } else {
      if (needSlash) src += '/'
      src += segToRegexSrc(seg)
      needSlash = true
    }
  }
  re = new RegExp(src + '$')
  compiled.set(pattern, re)
  return re
}

// Fallback for unsupported constructs: match anything under the pattern's
// leading literal segments. Over-matching adds a topic to a budget-capped
// brief (harmless); under-matching silently drops team context (harmful).
// Brace SEQUENCES ({a..b} / '..' inside braces) expand unpredictably enough to
// escape even the literal prefix (fuzz-found: 'a/{a,..}/**' matches 'x'), so
// they degrade all the way to match-everything.
// Pure-dot segments ('.', '..') carry path-collapse semantics in minimatch
// ('**/a/..' matches 'a' — fuzz-found) that no real anchor relies on; they take
// the conservative fallback too.
const DOT_SEG = /^\.{1,2}$/

function unsupportedFallback(pattern: string, file: string): boolean {
  if (/\{[^}]*\.\.[^}]*\}/.test(pattern)) return true
  const segs = pattern.split('/').filter((s) => s !== '')
  const lit: string[] = []
  for (const seg of segs) {
    if (UNSUPPORTED.test(seg) || /[*?]/.test(seg) || DOT_SEG.test(seg)) break
    lit.push(seg)
  }
  if (lit.length === 0) return true
  const prefix = lit.join('/')
  return file === prefix || file.startsWith(prefix + '/')
}

export function globMatchesFile(pattern: string, file: string): boolean {
  try {
    const segs = pattern.split('/')
    if (UNSUPPORTED.test(pattern) || segs.some((s) => DOT_SEG.test(s))) {
      return unsupportedFallback(pattern, file)
    }
    return globToRegExp(pattern).test(file)
  } catch {
    return false // a malformed pattern must never break the hook
  }
}

// where_files entries are segment-prefix anchors (same rule as the server's
// fileUnderPath): "src/auth" covers src/auth/x.ts but never src/auth-old/x.ts.
export function fileUnderPath(file: string, anchor: string): boolean {
  const a = anchor.replace(/\/+$/, '')
  return file === a || file.startsWith(a + '/')
}

export interface AnchoredTopic {
  anchors?: { patterns?: string[]; files?: string[] }
}

export function topicMatchesFile(t: AnchoredTopic, file: string): boolean {
  if (t.anchors?.patterns?.some((p) => globMatchesFile(p, file))) return true
  if (t.anchors?.files?.some((wf) => fileUnderPath(file, wf))) return true
  return false
}
